// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import HelpIcon from '@material-ui/icons/Help';
import {
  Divider,
  Typography,
  withStyles,
  Tooltip,
} from "@material-ui/core";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { push } from 'connected-react-router';
import { AlertMessages, FormActionButtons, FormTextField, Loading, Switch } from '../../components';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  FAIL,
  SUCCESS,
} from "../../constants";
import {
  fetchAPISettingsSpecDefaultProxy,
  fetchAPISettingsEnforceCategoryOrdering,
  updateAPISettings,
  updateAPISettingSpecDefaultProxy,
} from '../../actions/apiSettings';
import {
  getIsError,
  getErrors,
  getIsLoading,
  getAPISettings,
  getUpdateAPISettingsStatus,
  getupdateAPISettingSpecDefaultProxyStatus,
  getAPISettingsSpecDefaultProxy,
  getAPISettingsEnforceCategoryOrdering,
  getSpecErrorMsg,
} from '../../reducers/apiSettings';
import { isEmpty } from "lodash";
import { getUserDetails } from '../../reducers/portalConfig';
import styles from "./styles";
import { getI18n } from "../../utils/intl";

export const ApiSettings = (props) => {
  const {
    classes,
    fetchAPISettingsEnforceCategoryOrdering,
    fetchAPISettingsSpecDefaultProxy,
    isLoading,
    updateAPISettings,
    updateAPISettingSpecDefaultProxy,
    userContext = {},
    initialSpecDefaultProxy,
    initialEnforceCategoryOrdering,
    updateStatus,
    updateAPISettingSpecDefaultProxyStatus,
    errors,
    specErrors,
  } = props;

  const intl = getI18n(useIntl());

  const [specDefaultProxy, setSpecDefaultProxy] = useState("");
  const [enforceCategoryOrdering, setEnforceCategoryOrdering] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [formHasErrors, setFormHasErrors] = useState(false);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setSpecDefaultProxy(initialSpecDefaultProxy);
  },[initialSpecDefaultProxy]);

  useEffect(() => {
    setEnforceCategoryOrdering(initialEnforceCategoryOrdering);
  },[initialEnforceCategoryOrdering]);

  useEffect(() => {
    fetchAPISettingsEnforceCategoryOrdering();
    fetchAPISettingsSpecDefaultProxy();
  }, []);

  useEffect(() => {
    if (!userContext.portalAdmin) {
      props.push('/404');
    }
  }, [userContext]);

  const onCancel = () => {
    setEnforceCategoryOrdering(initialEnforceCategoryOrdering)
    setSpecDefaultProxy(initialSpecDefaultProxy);
    fetchAPISettingsEnforceCategoryOrdering();
    fetchAPISettingsSpecDefaultProxy();
  };

  const onSave = () => {
    updateAPISettings(enforceCategoryOrdering);
    updateAPISettingSpecDefaultProxy(specDefaultProxy);
  };

  useEffect(() => {
    if (updateStatus === SUCCESS && updateAPISettingSpecDefaultProxyStatus === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.api.settings.update.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
      fetchAPISettingsEnforceCategoryOrdering();
      fetchAPISettingsSpecDefaultProxy();
    } else if (updateStatus === FAIL || updateAPISettingSpecDefaultProxyStatus === FAIL) {
      if (updateAPISettingSpecDefaultProxyStatus === FAIL)
      {
        if(specErrors) {
          notifyMessages(ALERT_ERROR, `${intl.getI18nMessage('label.settings.api.default.proxy')} : ${specErrors}`);
        } else {
          notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.settings.api.settings.update.error'));
        }
      } else {
        notifyMessages(ALERT_ERROR, errors || intl.getI18nMessage('label.settings.api.settings.update.error'));
      }
    }
  }, [updateStatus, updateAPISettingSpecDefaultProxyStatus]);

  const TooltipIconForSpecDefaultProxy = () => (
    <div className={classes.helpIconContainer}>
      <div>
        {intl.getI18nMessage('label.settings.api.default.proxy')}
      </div>
      <Tooltip classes={classes} title={intl.getI18nMessage('label.settings.api.default.proxy.hint')} arrow placement="right">
        <HelpIcon className={classes.helpIcon}/>
      </Tooltip>
    </div>
  );

  const handleChange = (value) => {
    setSpecDefaultProxy(value);
    const trimmedValue = value.trim();
    if (trimmedValue) {
      // eslint-disable-next-line
      const pattern = /^((http(s)?:\/\/){1}([A-Za-z0-9\-]+){1}(\.[A-Za-z0-9\-]+)*(:\d{1,5})?|(([A-Za-z0-9\-]+)(\.([A-Za-z0-9\-])+)*){1})$/;
      setFormHasErrors(!pattern.test(trimmedValue));
    } else {
      setFormHasErrors(false);
    }
  };

  const handleInputChange = (val) => {
    setEnforceCategoryOrdering(val);
  }

  const formModified =
    (
      (!isEmpty(initialSpecDefaultProxy) || !isEmpty(specDefaultProxy))
      && (initialSpecDefaultProxy !== specDefaultProxy)
    )
    || (initialEnforceCategoryOrdering !== enforceCategoryOrdering);

  const helperText = formHasErrors ?
    intl.getI18nMessage("label.settings.api.default.proxy.errortext")
    :
    intl.getI18nMessage("label.settings.api.default.proxy.helptext");

  const renderSpecDefaultProxy = () =>
    (<Fragment>
      <Typography variant="h1" className={classes.pageTitle}>
        {intl.getI18nMessage("label.settings.api.settings")}
      </Typography>
      <div>
        <FormTextField
          fieldContainerClass={classes.categoryField}
          name={(<TooltipIconForSpecDefaultProxy />)}
          id={'api-settings-spec-default-proxy'}
          data-apim-test={'api-settings-spec-default-proxy'}
          error={formHasErrors}
          errorHelperText={formHasErrors}
          variant="standard"
          value={specDefaultProxy}
          handleChange={handleChange}
          helperText={helperText}
          maxLength={"255"}
          textFieldClass={classes.categoryTextField}
          type={'string'}
      />
      </div>
    </Fragment>);


  const renderTemplateCategory = () =>
    (<Fragment>
        <div className={classes.templateSubtitleContainer}>
          <div>
            <Typography variant="h4">
              {intl.getI18nMessage("label.settings.api.policy.template.category.order")}
            </Typography>
          </div>
          <div>
            <Switch
              checked={enforceCategoryOrdering}
              id={'api-settings-template-category-ordering'}
              onChange={(checked) => handleInputChange(checked)}
              data-apim-test={'api-settings-template-category-ordering'}
            />
          </div>
        </div>
        <Divider />
        <Typography className={classes.switchHelpText} variant="body1">
          {intl.getI18nMessage("label.settings.api.policy.template.category.order.helpertext")}
        </Typography>
    </Fragment>);


  return(
    <div className={classes.details}>
      {(isLoading)
        && <Loading pageLoader={true} />}
      {renderSpecDefaultProxy()}
      {renderTemplateCategory()}
      {formModified && <FormActionButtons
        id={"api-settings"}
        isContainerFooter
        cancelText={intl.getI18nMessage("label.cancel.button")}
        isDisabled={formHasErrors}
        nextText={intl.getI18nMessage("label.save.button")}
        onCancelClick={onCancel}
        onNextClick={onSave}
      />}
      {notificationMessage !=='' && (
        <AlertMessages
        id={'api-settings-alert-messages'}
        variant={notificationStatus}
        message={notificationMessage}
        containerClass={classes.notificationWidthClass}
        onClose={() => notifyMessages('', '')}
        hasNotificationIcon={true}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  isError: getIsError(state),
  specErrors: getSpecErrorMsg(state),
  errors: getErrors(state),
  isLoading: getIsLoading(state),
  apiSettings: getAPISettings(state),
  updateStatus: getUpdateAPISettingsStatus(state),
  updateAPISettingSpecDefaultProxyStatus: getupdateAPISettingSpecDefaultProxyStatus(state),
  userContext: getUserDetails(state),
  initialSpecDefaultProxy: getAPISettingsSpecDefaultProxy(state),
  initialEnforceCategoryOrdering: getAPISettingsEnforceCategoryOrdering(state),
});

const mapDispatchToProps = {
  updateAPISettings,
  fetchAPISettingsSpecDefaultProxy,
  fetchAPISettingsEnforceCategoryOrdering,
  getAPISettingsSpecDefaultProxy,
  getAPISettingsEnforceCategoryOrdering,
  updateAPISettingSpecDefaultProxy,
  push,
};

ApiSettings.propTypes = {
  classes: PropTypes.object,
  isError: PropTypes.bool,
  errors: PropTypes.array,
  specErrors: PropTypes.string,
  isLoading: PropTypes.bool,
  apiSettings: PropTypes.object,
  updateStatus: PropTypes.string,
  updateAPISettingSpecDefaultProxyStatus: PropTypes.string,
  fetchAPISettingsEnforceCategoryOrdering: PropTypes.func,
  fetchAPISettingsSpecDefaultProxy: PropTypes.func,
  updateAPISettings: PropTypes.func,
  userContext: PropTypes.object,
  push: PropTypes.func,
  getAPISettingsSpecDefaultProxy: PropTypes.func,
  initialSpecDefaultProxy: PropTypes.string,
  getAPISettingsEnforceCategoryOrdering: PropTypes.func,
  initialEnforceCategoryOrdering: PropTypes.bool,
  updateAPISettingSpecDefaultProxy: PropTypes.func,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps))(ApiSettings);
